import React, { useState, useEffect, useRef, forwardRef } from 'react';
import {
  Modal,
  Button,
  Form,
  Col,
  Row,
  Table,
  Spinner,
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import Select from 'react-select';
import { useCurrency } from '../../../../context/currencyContext';
import Swal from 'sweetalert2';
import './creditMemo.css';

// ============================================================
// THEME & CONSTANTS
// ============================================================

const themeColor = '#004aad';
const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

// ============================================================
// CREDIT TYPE OPTIONS
// ============================================================

const creditTypeOptions = [
  { value: 'refund', label: 'Refund' },
  { value: 'adjustment', label: 'Adjustment' },
  { value: 'goodwill', label: 'Goodwill' },
  { value: 'other', label: 'Other' },
  { value: 'alloy', label: 'Alloy' },
  { value: 'bad_debt_write_off', label: 'Bad Debt Write-Off' },
  { value: 'ceramic', label: 'Ceramic' },
  { value: 'crown_bridge', label: 'Crown & Bridge' },
  { value: 'denture', label: 'Denture' },
  { value: 'general', label: 'General' },
  { value: 'interest_reversal', label: 'Interest Reversal' },
  { value: 'models', label: 'Models' },
  { value: 'prompt_payment', label: 'Prompt Payment' },
  { value: 'volume_discount', label: 'Volume Discount' },
];

// ============================================================
// COMPONENT
// ============================================================

function CreditMemo({ show, handleClose }) {
  const { formatAmount } = useCurrency();

  const [userLabId, setUserLabId] = useState('');
  const [backendError, setBackendError] = useState(false);

  const [creditDate, setCreditDate] = useState(new Date());

  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [accountName, setAccountName] = useState('');

  const [amount, setAmount] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [creditType, setCreditType] = useState('');
  const [memo, setMemo] = useState('');

  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [error, setError] = useState(null);

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);

  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [appliedAmount, setAppliedAmount] = useState(0);
  const [unappliedAmount, setUnappliedAmount] = useState(0);

  const ignoreAutoApplyRef = useRef(false);

  // USER LAB ID
  useEffect(() => {
    if (show) {
      const storedUserLabId = localStorage.getItem('userLabId');

      if (storedUserLabId) {
        setUserLabId(String(storedUserLabId).trim());
        setBackendError(false);
      } else {
        setError('User identification not found. Please log in again.');
      }
    }
  }, [show]);

  // SELECT STYLING
  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: '#fff',
      border: '1px solid #dfe1e6',
      borderRadius: '3px',
      minHeight: '36px',
      height: '36px',
      boxShadow: 'none',
      fontSize: '0.875rem',
      '&:hover': {
        border: '1px solid #dfe1e6',
      },
    }),

    valueContainer: (provided) => ({
      ...provided,
      height: '36px',
      padding: '0 8px',
    }),

    indicatorsContainer: (provided) => ({
      ...provided,
      height: '36px',
    }),

    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      borderRadius: '3px',
      border: '1px solid #dfe1e6',
      boxShadow: '0 0.5rem 1rem rgba(0,0,0,.15)',
    }),

    option: (provided, state) => ({
      ...provided,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: '0.875rem',
      backgroundColor: state.isSelected ? themeColor : 'white',
      color: state.isSelected ? 'white' : 'black',

      '&:hover': {
        backgroundColor: state.isSelected ? themeColor : '#f4f5f7',
        color: state.isSelected ? 'white' : 'black',
      },
    }),

    singleValue: (provided) => ({
      ...provided,
      color: '#172b4d',
    }),

    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
    }),
  };

  // FETCH ACCOUNTS
  useEffect(() => {
    const fetchAccounts = async () => {
      setLoadingAccounts(true);
      setError(null);

      if (!userLabId) {
        setLoadingAccounts(false);
        setAccounts([]);
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/account/lab-accounts/${userLabId}`
        );

        if (!response.ok) {
          if (response.status === 404) {
            setAccounts([]);
          } else {
            throw new Error(`Failed to fetch accounts: ${response.status}`);
          }
        } else {
          const result = await response.json();

          if (result.success && Array.isArray(result.data)) {
            const sortedAccounts = [...result.data].sort((a, b) =>
              String(a.account_name || '')
                .toLowerCase()
                .localeCompare(String(b.account_name || '').toLowerCase())
            );

            setAccounts(sortedAccounts);
            setBackendError(false);
          } else if (
            result.success &&
            result.message === 'No accounts found for this lab.'
          ) {
            setAccounts([]);
          } else {
            throw new Error('Invalid data format');
          }
        }
      } catch (fetchError) {
        setBackendError(true);
        setError('Failed to connect to server.');
        setAccounts([]);
      } finally {
        setLoadingAccounts(false);
      }
    };

    if (show && userLabId) {
      fetchAccounts();
    } else if (show && !userLabId) {
      setLoadingAccounts(false);
      setAccounts([]);
    } else if (!show) {
      resetFormState();
    }
  }, [show, userLabId]);

  // ACCOUNT CHANGE
  useEffect(() => {
    if (selectedAccount) {
      const account = accounts.find((acc) => acc._id === selectedAccount);

      setSelectedAccountId(account ? account.accountId : '');
      setAccountName(account ? account.account_name : '');
    } else {
      setSelectedAccountId('');
      setAccountName('');
      setSelectedInvoices([]);
      setReferenceNo('');
      setAmount('');
      setTableData([]);
      setAppliedAmount(0);
      setUnappliedAmount(0);
    }
  }, [selectedAccount, accounts]);

  // INVOICE NUMBER PARSER
  const parseInvoiceNumber = (invoiceNumber) => {
    if (!invoiceNumber) {
      return { prefix: '', number: 0 };
    }

    const match = invoiceNumber.toString().match(/(\D*)(\d+)/);

    return match
      ? {
          prefix: match[1] || '',
          number: parseInt(match[2], 10) || 0,
        }
      : {
          prefix: invoiceNumber.toString(),
          number: 0,
        };
  };

  // FETCH INVOICES
  useEffect(() => {
    const fetchInvoices = async (accountId) => {
      if (!accountId) {
        setTableData([]);
        setSelectedInvoices([]);
        setReferenceNo('');
        setAmount('');
        setAppliedAmount(0);
        setUnappliedAmount(0);
        return;
      }

      setLoadingInvoices(true);
      setInvoiceError(null);

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/invoices/invoices/account?accountId=${accountId}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch invoices: ${response.status}`);
        }

        const invoices = await response.json();

        const formattedInvoices = invoices.map((invoice) => {
          const remaining = Math.max(
            0,
            (invoice.total || 0) - (invoice.paid || 0)
          );

          const parsedInvoiceNumber = parseInvoiceNumber(invoice.invoiceNumber);

          return {
            id: invoice._id || invoice.invoiceNumber,
            invoiceNumber: invoice.invoiceNumber,
            description: `Invoice ${invoice.invoiceNumber}`,
            caseNumber: invoice.caseNumber || 'Case 43791',
            invoiceDate: format(new Date(invoice.invoiceDate), 'd MMM yyyy'),
            total: invoice.total || 0,
            paid: invoice.paid || 0,
            originalRemaining: remaining,
            remaining: remaining,
            payment: 0,
            parsedInvoiceNumber,
          };
        });

        let filteredInvoices = formattedInvoices.filter(
          (inv) => inv.remaining > 0
        );

        filteredInvoices.sort((a, b) => {
          if (a.parsedInvoiceNumber.prefix < b.parsedInvoiceNumber.prefix) {
            return -1;
          }

          if (a.parsedInvoiceNumber.prefix > b.parsedInvoiceNumber.prefix) {
            return 1;
          }

          return a.parsedInvoiceNumber.number - b.parsedInvoiceNumber.number;
        });

        const finalInvoices = filteredInvoices.map(
          ({ parsedInvoiceNumber, ...rest }) => rest
        );

        setTableData(finalInvoices);
        setSelectedInvoices([]);
      } catch (err) {
        setInvoiceError('Error fetching invoices: ' + err.message);
        setTableData([]);
        setSelectedInvoices([]);
        setAppliedAmount(0);
        setUnappliedAmount(0);
      } finally {
        setLoadingInvoices(false);
      }
    };

    if (userLabId && !backendError) {
      fetchInvoices(selectedAccountId);
    }
  }, [selectedAccountId, userLabId, backendError]);

  // APPLIED / UNAPPLIED
  useEffect(() => {
    const totalApplied = tableData.reduce(
      (sum, row) => sum + parseFloat(row.payment || 0),
      0
    );

    setAppliedAmount(totalApplied);

    const numericAmount = parseFloat(amount || 0);

    setUnappliedAmount(
      !isNaN(numericAmount) && numericAmount > totalApplied
        ? numericAmount - totalApplied
        : 0
    );
  }, [amount, tableData]);

  // AUTO AMOUNT
  useEffect(() => {
    if (selectedInvoices.length > 0) {
      const totalPayment = selectedInvoices.reduce((sum, invoiceId) => {
        const invoice = tableData.find((row) => row.id === invoiceId);

        return sum + (invoice ? parseFloat(invoice.payment || 0) : 0);
      }, 0);

      ignoreAutoApplyRef.current = true;

      setAmount(totalPayment > 0 ? totalPayment.toString() : '');
    } else if (!selectedAccountId) {
      setAmount('');
    }
  }, [selectedInvoices, tableData]);

  // AUTO APPLY AMOUNT TO INVOICES
  useEffect(() => {
    if (ignoreAutoApplyRef.current) {
      ignoreAutoApplyRef.current = false;
      return;
    }

    if (
      amount &&
      !isNaN(parseFloat(amount)) &&
      selectedAccountId &&
      tableData.length > 0 &&
      userLabId &&
      !backendError
    ) {
      let remainingAmount = parseFloat(amount);
      const newSelectedInvoices = [];

      const updatedTableData = tableData.map((row) => ({
        ...row,
        payment: 0,
        remaining: row.originalRemaining,
      }));

      const finalTableData = updatedTableData.map((row) => {
        if (remainingAmount <= 0) return row;

        if (row.originalRemaining > 0) {
          if (remainingAmount >= row.originalRemaining) {
            newSelectedInvoices.push(row.id);
            remainingAmount -= row.originalRemaining;

            return {
              ...row,
              payment: row.originalRemaining,
              remaining: 0,
            };
          } else {
            newSelectedInvoices.push(row.id);

            const paymentAmount = remainingAmount;

            remainingAmount = 0;

            return {
              ...row,
              payment: paymentAmount,
              remaining: row.originalRemaining - paymentAmount,
            };
          }
        }

        return row;
      });

      setSelectedInvoices(newSelectedInvoices);
      setTableData(finalTableData);
    } else if (!amount) {
      setSelectedInvoices([]);
      setTableData(
        tableData.map((row) => ({
          ...row,
          payment: 0,
          remaining: row.originalRemaining,
        }))
      );
    }
  }, [amount, selectedAccountId, userLabId, backendError]);

  // RESET
  const resetFormState = () => {
    setCreditDate(new Date());
    setSelectedAccount('');
    setSelectedAccountId('');
    setAccountName('');
    setAmount('');
    setReferenceNo('');
    setCreditType('');
    setMemo('');
    setFormSubmitted(false);
    setError(null);
    setTableData([]);
    setLoadingInvoices(false);
    setInvoiceError(null);
    setSelectedInvoices([]);
    setAppliedAmount(0);
    setUnappliedAmount(0);
    setBackendError(false);
  };

  const handleCloseModal = () => {
    resetFormState();
    handleClose();
  };

  // INPUT HANDLERS
  const handleAccountChange = (selectedOption) => {
    setSelectedAccount(selectedOption ? selectedOption.value : '');
    setAccountName(selectedOption ? selectedOption.label : '');
  };

  const handleAmountChange = (e) => setAmount(e.target.value);
  const handleRefNoChange = (e) => setReferenceNo(e.target.value);
  const handleCreditTypeChange = (e) => setCreditType(e.target.value);
  const handleMemoChange = (e) => setMemo(e.target.value);
  const handleDateChange = (date) => setCreditDate(date);

  // INVOICE SELECTION
  const handleInvoiceSelection = (invoiceId) => {
    const isSelected = selectedInvoices.includes(invoiceId);

    setSelectedInvoices((prev) =>
      isSelected ? prev.filter((id) => id !== invoiceId) : [...prev, invoiceId]
    );

    setTableData(
      tableData.map((row) =>
        row.id === invoiceId
          ? {
              ...row,
              payment: isSelected ? 0 : row.originalRemaining,
              remaining: isSelected ? row.originalRemaining : 0,
            }
          : row
      )
    );
  };

  // INDIVIDUAL INVOICE PAYMENT
  const handleInvoicePaymentChange = (index, paymentValue) => {
    const updatedTableData = [...tableData];

    const row = updatedTableData[index];

    const newPayment = parseFloat(paymentValue || 0);

    if (row) {
      const appliedPayment = Math.min(newPayment, row.originalRemaining);

      updatedTableData[index] = {
        ...row,
        payment: appliedPayment,
        remaining: row.originalRemaining - appliedPayment,
      };

      setTableData(updatedTableData);
    }
  };

  // SAVE CREDIT MEMO
  const handleSave = async (e) => {
    if (e) e.preventDefault();

    setFormSubmitted(true);

    if (!userLabId) {
      Swal.fire({
        title: 'Error!',
        text: 'Unable to process credit: User identification not found.',
        icon: 'error',
        confirmButtonColor: themeColor,
      });

      return;
    }

    if (!selectedAccount || !amount || !creditDate || !creditType) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill in all required fields (*)',
        icon: 'warning',
        confirmButtonColor: themeColor,
      });

      return;
    }

    const numericAmount = parseFloat(amount || 0);

    if (isNaN(numericAmount) || numericAmount <= 0) {
      Swal.fire({
        title: 'Invalid Amount',
        text: 'Please enter a valid positive amount.',
        icon: 'warning',
        confirmButtonColor: themeColor,
      });

      return;
    }

    const appliedInvoices = tableData
      .filter(
        (row) =>
          selectedInvoices.includes(row.id) && parseFloat(row.payment || 0) > 0
      )
      .map((row) => ({
        invoiceNumber: row.invoiceNumber,
        appliedAmount: parseFloat(row.payment || 0),
        newPaidAmount:
          parseFloat(row.paid || 0) + parseFloat(row.payment || 0),
      }));

    const creditData = {
      accountId: selectedAccountId,
      accountName,
      amount: numericAmount,
      creditDate: format(creditDate, 'yyyy-MM-dd'),
      creditType,
      referenceNo,
      memo,
      appliedInvoices,
      finalAppliedAmount: appliedAmount,
      finalUnappliedAmount: unappliedAmount,
      addedBy: userLabId,
      uniqueCreditId: `CRD-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/credit-memos/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(creditData),
      });

      let responseData;

      try {
        responseData = JSON.parse(await response.text());
      } catch (e) {
        throw new Error('Invalid JSON response');
      }

      if (!response.ok) {
        throw new Error(
          responseData.message ||
            `Failed to create credit memo: ${response.status}`
        );
      }

      if (responseData.success) {
        Swal.fire({
          title: 'Success!',
          text: responseData.message || 'Credit memo created successfully!',
          icon: 'success',
          confirmButtonColor: themeColor,
        });

        handleCloseModal();
      } else {
        throw new Error(
          responseData.message || 'Failed to create credit memo'
        );
      }
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'Failed to create credit memo: ' + error.message,
        icon: 'error',
        confirmButtonColor: themeColor,
      });
    }
  };

  // CUSTOM DATE INPUT WITH CALENDAR ICON
  const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
    <div className="credit-memo-date-container" onClick={onClick}>
      <input
        type="text"
        value={value || ''}
        ref={ref}
        readOnly
        className="form-control credit-memo-date-input"
      />

      <button type="button" className="credit-memo-calendar-btn">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
          <line x1="7" y1="14" x2="7" y2="14"></line>
          <line x1="12" y1="14" x2="12" y2="14"></line>
          <line x1="17" y1="14" x2="17" y2="14"></line>
          <line x1="7" y1="18" x2="7" y2="18"></line>
          <line x1="12" y1="18" x2="12" y2="18"></line>
          <line x1="17" y1="18" x2="17" y2="18"></line>
        </svg>
      </button>
    </div>
  ));

  const accountOptions = accounts.map((acc) => ({
    value: acc._id,
    label: acc.account_name,
  }));

  return (
    <Modal
      show={show}
      onHide={handleCloseModal}
      size="lg"
      centered
      className="credit-memo-modal-root"
    >
      <Modal.Header closeButton className="credit-memo-modal-header" />

      <Modal.Body className="credit-memo-modal-body">
        <div className="credit-memo-main-header">Create New Credit</div>

        <h5 className="credit-memo-section-title">Credit Details</h5>

        <div className="credit-memo-details-box">
          <Row>
            <Col md={6}>
              <Form.Group className="credit-memo-field-row">
                <Form.Label className="credit-memo-field-label">
                  Account<span className="credit-memo-required">*</span>
                </Form.Label>

                <div className="credit-memo-field-value">
                  <Select
                    classNamePrefix="cm-select"
                    options={accountOptions}
                    value={
                      accountOptions.find(
                        (opt) => opt.value === selectedAccount
                      ) || null
                    }
                    onChange={handleAccountChange}
                    isLoading={loadingAccounts}
                    placeholder="Select Account..."
                    styles={customStyles}
                    isClearable
                  />

                  {formSubmitted && !selectedAccount && (
                    <span className="text-danger small d-block">
                      Account is required
                    </span>
                  )}
                </div>
              </Form.Group>

              <Form.Group className="credit-memo-field-row">
                <Form.Label className="credit-memo-field-label">
                  Credit Date<span className="credit-memo-required">*</span>
                </Form.Label>

                <div className="credit-memo-field-value">
                  <DatePicker
                    selected={creditDate}
                    onChange={handleDateChange}
                    dateFormat="d MMM yyyy"
                    customInput={<CustomDateInput />}
                  />
                </div>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="credit-memo-field-row">
                <Form.Label className="credit-memo-field-label">
                  Reference No.
                </Form.Label>

                <div className="credit-memo-field-value">
                  <Form.Control
                    type="text"
                    placeholder="Optional - auto-gene"
                    value={referenceNo}
                    onChange={handleRefNoChange}
                    className="credit-memo-form-input"
                  />
                </div>
              </Form.Group>

              <Form.Group className="credit-memo-field-row">
                <Form.Label className="credit-memo-field-label">
                  Amount<span className="credit-memo-required">*</span>
                </Form.Label>

                <div className="credit-memo-field-value">
                  <Form.Control
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={handleAmountChange}
                    className="credit-memo-form-input"
                  />

                  {formSubmitted && !amount && (
                    <span className="text-danger small d-block">
                      Amount is required
                    </span>
                  )}
                </div>
              </Form.Group>

              <Form.Group className="credit-memo-field-row">
                <Form.Label className="credit-memo-field-label">
                  Type<span className="credit-memo-required">*</span>
                </Form.Label>

                <div className="credit-memo-field-value">
                  <Form.Select
                    value={creditType}
                    onChange={handleCreditTypeChange}
                    className="credit-memo-form-input"
                  >
                    <option value="">Select Credit Type</option>

                    {creditTypeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>

                  {formSubmitted && !creditType && (
                    <span className="text-danger small d-block">
                      Credit Type is required
                    </span>
                  )}
                </div>
              </Form.Group>
            </Col>
          </Row>
        </div>

        <div className="credit-memo-optional-title">
          Apply to Outstanding Invoices (Optional)
        </div>

        <div className="credit-memo-table-container">
          {loadingInvoices ? (
            <div className="text-center p-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Table responsive hover className="credit-memo-invoice-table">
              <thead>
                <tr>
                  <th
                    className="credit-memo-table-header"
                    style={{ width: '40px' }}
                  ></th>

                  <th className="credit-memo-table-header">Description</th>

                  <th className="credit-memo-table-header">Invoice Date</th>

                  <th className="credit-memo-table-header text-end">Total</th>

                  <th className="credit-memo-table-header text-end">
                    Remaining
                  </th>

                  <th
                    className="credit-memo-table-header text-end"
                    style={{ width: '120px' }}
                  >
                    Credit
                  </th>
                </tr>
              </thead>

              <tbody>
                {tableData.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="text-center py-3 credit-memo-table-cell"
                    >
                      No outstanding invoices found for this account.
                    </td>
                  </tr>
                ) : (
                  tableData.map((row, idx) => (
                    <tr key={row.id}>
                      <td className="credit-memo-table-cell">
                        <Form.Check
                          type="checkbox"
                          checked={selectedInvoices.includes(row.id)}
                          onChange={() => handleInvoiceSelection(row.id)}
                        />
                      </td>

                      <td className="credit-memo-table-cell">
                        <div>{row.description}</div>

                        <div className="credit-memo-sub-text">
                          {row.caseNumber}
                        </div>
                      </td>

                      <td className="credit-memo-table-cell">
                        {row.invoiceDate}
                      </td>

                      <td className="credit-memo-table-cell text-end">
                        {formatAmount(row.total)}
                      </td>

                      <td className="credit-memo-table-cell text-end">
                        {formatAmount(row.remaining)}
                      </td>

                      <td className="credit-memo-table-cell">
                        <div className="credit-memo-cell-wrapper">
                          <Form.Control
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={row.payment || ''}
                            onChange={(e) =>
                              handleInvoicePaymentChange(idx, e.target.value)
                            }
                            disabled={!selectedInvoices.includes(row.id)}
                            className="credit-memo-credit-input"
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </div>

        <Row className="mt-3 credit-memo-bottom-row">
          <Col md={6} className="credit-memo-bottom-col">
            <Form.Group style={{ width: '100%' }}>
  <Form.Label className="credit-memo-label">Memo</Form.Label>

  <textarea
    rows={5}
    placeholder="Add any additional notes about this credit memo..."
    value={memo}
    onChange={handleMemoChange}
    className="credit-memo-textarea"
  />
</Form.Group>
          </Col>

          <Col
            md={6}
            className="d-flex flex-column justify-content-center credit-memo-bottom-col credit-memo-summary-col"
          >
            <div className="credit-memo-summary-row">
              <span className="credit-memo-summary-label">Total Amount</span>

              <span className="credit-memo-summary-value">
                {formatAmount(parseFloat(amount || 0))}
              </span>
            </div>

            <div className="credit-memo-summary-row">
              <span className="credit-memo-summary-label">Applied</span>

              <span className="credit-memo-summary-value">
                {formatAmount(appliedAmount)}
              </span>
            </div>

            <div className="credit-memo-summary-row">
              <span className="credit-memo-summary-label">Unapplied</span>

              <span className="credit-memo-summary-value">
                {formatAmount(unappliedAmount)}
              </span>
            </div>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer className="credit-memo-modal-footer">
        <Button
          variant="light"
          onClick={handleCloseModal}
          className="credit-memo-cancel-button"
        >
          Cancel
        </Button>

        <Button className="credit-memo-save-button" onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CreditMemo;